import { ChangeEvent, FocusEvent, FocusEventHandler, useEffect, useRef, useState } from "react";

type UseDebouncedInputParams = {
  value: string;
  onChange?: {
    (e: ChangeEvent<any>): void;
    <T = string | ChangeEvent<any>>(field: T): T extends ChangeEvent<any>
      ? void
      : (e: string | ChangeEvent<any>) => void;
  };
  onBlur: FocusEventHandler<HTMLInputElement> | undefined;
  name: string;
  shouldDebounce?: boolean;
  disableWhiteSpacesTrim?: boolean;
};

export const useDebouncedInput = (
  { value, onChange, name, onBlur, shouldDebounce, disableWhiteSpacesTrim }: UseDebouncedInputParams,
  ms = 300,
) => {
  const [localState, setLocalState] = useState(value);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setLocalState(value);
  }, [value]);

  const handleFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = disableWhiteSpacesTrim ? e.target.value : e.target.value.trimStart();

    if (!shouldDebounce) {
      onChange?.({
        target: {
          name,
          value: inputValue,
          files: e.target.files,
        },
        currentTarget: {
          name,
          value: inputValue,
          files: e.target.files,
        },
      });
    } else {
      setLocalState(inputValue);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        onChange?.({
          target: {
            name,
            value: inputValue,
            files: e.target.files,
          },
          currentTarget: {
            name,
            value: inputValue,
            files: e.target.files,
          },
        });
      }, ms);
    }
  };

  const handleFieldBlur = (e: FocusEvent<HTMLInputElement, Element>) => {
    onBlur?.(e);
    if (disableWhiteSpacesTrim) return; // Preserve spaces for password fields; do not trim
    // For onChange: after the field loses focus, trim any leading and trailing spaces.

    // When the input has a default numeric value,
    // clicking in and then out cause a "trim is not a function" error
    // because localState holds a number at that point
    const trimmedValue = typeof localState === "string" ? localState.trim() : localState;

    const syntheticEvent = {
      target: { name, value: trimmedValue },
      currentTarget: { name, value: trimmedValue },
    };

    if (!shouldDebounce) return onChange?.(syntheticEvent);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    onChange?.(syntheticEvent);
  };
  return {
    localState: shouldDebounce ? localState : value,
    handleFieldChange,
    handleFieldBlur,
  };
};
